// Query keys + the displayed-state overlay for the broadcast console. Same shape
// as `live-in-club/ui/live-in-club-page/query-key.ts` — extracted so the route
// loader and the console's molecules share one definition, partitioned by `clubId`
// even though the server fn resolves its own scope (the post-sign-out stale-data
// fix).

import type { BroadcastState, StreamStats } from '../../domain/types';

export const streamConfigQueryKey = (clubId: string) =>
	['streaming', 'config', clubId] as const;

export const streamStatsQueryKey = (clubId: string) =>
	['streaming', 'stats', clubId] as const;

// A locally-initiated broadcast transition and the instant it fired. The overlay
// holds the displayed state at `target` until the poll CONFIRMS it — not until
// the HTTP write returns, which is what let a stale poll flap the badge back.
export interface LocalTransition {
	target: BroadcastState;
	at: number;
}

// The displayed state holds the local target for at most this long. After it
// the poll wins unconditionally, so a transition the platform never confirmed
// (a failed write) can never stick the UI forever.
export const TRANSITION_SAFETY_WINDOW_MS = 30_000;

// `getStats` reads a media status the platform caches for ~15s, so a poll
// fetched sooner than this after a transition may still carry PRE-transition
// state. Only a poll fetched past this window can confirm or contradict the
// local intent — the discriminator that stops a stale poll from tearing a fresh
// session down or flashing the rejoin banner.
export const SERVER_CACHE_WINDOW_MS = 15_000;

// A poll can speak to a transition only once the server cache that predates the
// transition has certainly expired (fetch time is past the transition + cache
// window). A poll older than that is a pre-transition snapshot, never truth
// about it.
function pollPostdatesTransition(
	transition: LocalTransition,
	pollUpdatedAt: number,
): boolean {
	return pollUpdatedAt >= transition.at + SERVER_CACHE_WINDOW_MS;
}

/**
 * True once a fresh poll AGREES with the local target — reality has caught up
 * and the hold is over. Used by the page to drop the transition so a later
 * contradictory poll (a real cross-device change) is respected immediately.
 */
export function isTransitionConfirmed(
	transition: LocalTransition | null,
	poll: StreamStats | null,
	pollUpdatedAt: number,
): boolean {
	if (!transition || !poll) return false;
	return (
		pollPostdatesTransition(transition, pollUpdatedAt) &&
		poll.state === transition.target
	);
}

/**
 * The displayed broadcast state is a READ-TIME OVERLAY, not an optimistic cache
 * write. Under the media-first model the client holds a pending target ahead of
 * the confirmed server value, so the badge is computed each render — never
 * mutated into the stats cache, where a stale optimistic value could outlive its
 * write. The reconciliation (ADR 0016 Decision 6, as amended):
 *
 *   1. with no local transition, the poll is authoritative;
 *   2. a fresh poll (past the server cache window) that reads the target CONFIRMS
 *      it — reality wins, the overlay ends;
 *   3. otherwise, while inside the safety window, the local target holds — a
 *      contradictory or stale poll (including a stale `offline` that predates the
 *      transition) is not truth, which is what stops the badge flapping and the
 *      rejoin banner flashing during a start/stop;
 *   4. once the safety window elapses the poll wins unconditionally, so a
 *      genuine external `offline` (the sweep) still tears the session down and a
 *      failed transition can never stick.
 *
 * The overlay only ever rewrites `state`; every other fact (`viewerCount`,
 * `startedAt`, `durationSec`) is the poll's, never fabricated.
 */
export function resolveDisplayedState(
	transition: LocalTransition | null,
	poll: StreamStats | null,
	pollUpdatedAt: number,
	now: number,
): StreamStats | null {
	if (!transition) return poll;
	if (isTransitionConfirmed(transition, poll, pollUpdatedAt)) return poll;
	if (now - transition.at < TRANSITION_SAFETY_WINDOW_MS) {
		return poll ? { ...poll, state: transition.target } : poll;
	}
	return poll;
}
